"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Button = exports.ButtonVariants = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const utilities_1 = require("@kalkulacka-one/design-system/utilities");
const react_1 = require("@headlessui/react");
const class_variance_authority_1 = require("class-variance-authority");
const react_2 = __importDefault(require("react"));
const icon_1 = require("./icon");
/*
 * Colour is expressed through the `primary`/`secondary`/`neutral` tokens —
 * the same names a themed instance already pins to restyle the "ano"/"ne"
 * meaning. This restyle only changes which formulas those names resolve to
 * (2026's flat hover/active steps and tint variants), not the names
 * themselves, so every existing caller keeps working unchanged.
 */
exports.ButtonVariants = (0, class_variance_authority_1.cva)([
    "ko:select-none ko:data-hover:cursor-pointer",
    "ko:font-semibold ko:tracking-[.01em]",
    "ko:rounded-pill",
    "ko:text-s",
    "ko:border-2",
    "ko:transition-[background-color,color,border-color,transform] ko:duration-base ko:data-active:duration-fast",
    "ko:data-disabled:cursor-not-allowed ko:data-disabled:opacity-45",
    "ko:grid ko:grid-flow-col ko:place-items-center ko:place-content-center ko:gap-1",
    "ko:data-active:scale-[0.97]",
    "ko:data-focus:outline-3 ko:data-focus:outline-offset-2 ko:data-focus:outline-focus/55",
], {
    variants: {
        size: {
            xsmall: "ko:h-9 ko:px-3",
            small: "ko:h-10 ko:px-4",
            // 2026's primary CTA ("Pokračovat"/"Začít odpovídat"/"Zobrazit výsledky") sizes its large
            // button at 0.9375rem→1.0625rem label (17px), 1rem/1.75rem padding and no explicit height —
            // the 56px/28px here reproduce that at our medium size, which is what those CTAs use.
            medium: "ko:h-14 ko:px-7 ko:text-[17px]",
        },
        variant: {
            fill: [""],
            outline: ["ko:bg-transparent"],
            link: ["ko:bg-transparent"],
            answer: ["ko:rounded-control ko:h-[clamp(58px,51.8776px+1.6327vw,78px)] ko:border-[1.5px] ko:bg-surface ko:px-6"],
            round: ["ko:rounded-full ko:aspect-square ko:h-[clamp(52px,46.4898px+1.4694vw,70px)] ko:w-[clamp(52px,46.4898px+1.4694vw,70px)] ko:border ko:bg-surface ko:p-0"],
            // The back/share pill: a light, hairline-bordered control that floats over
            // content (review/guide back links, result's back+share). Look is fixed —
            // it does not vary with `color`, unlike `fill`/`outline`/`link` (the colour
            // override lives in compoundVariants below, after the `color` classes).
            pill: ["ko:h-10 ko:gap-1.5 ko:border ko:bg-surface ko:px-4 ko:text-sm"],
        },
        color: {
            primary: ["ko:border-primary", "ko:data-disabled:border-primary"],
            secondary: ["ko:border-secondary", "ko:data-disabled:border-secondary"],
            neutral: ["ko:border-neutral", "ko:data-disabled:border-neutral"],
        },
    },
    defaultVariants: {
        size: "medium",
        variant: "fill",
        color: "primary",
    },
    compoundVariants: [
        {
            variant: "link",
            class: ["ko:border-transparent", "ko:data-disabled:border-transparent"],
        },
        {
            variant: "fill",
            color: "primary",
            class: [
                "ko:bg-primary",
                "ko:text-on-bg-primary",
                "ko:data-hover:bg-primary-hover",
                "ko:data-focus:bg-primary-hover",
                "ko:data-active:bg-primary-active",
                "ko:data-hover:data-active:bg-primary-active",
            ],
        },
        {
            variant: "fill",
            color: "secondary",
            class: [
                "ko:bg-secondary",
                "ko:text-on-bg-secondary",
                "ko:data-hover:bg-secondary-hover",
                "ko:data-focus:bg-secondary-hover",
                "ko:data-active:bg-secondary-active",
                "ko:data-hover:data-active:bg-secondary-active",
            ],
        },
        {
            variant: "fill",
            color: "neutral",
            class: ["ko:bg-neutral", "ko:text-on-bg-neutral", "ko:data-hover:bg-neutral/90", "ko:data-focus:bg-neutral/90", "ko:data-active:bg-neutral/80", "ko:data-hover:data-active:bg-neutral/80"],
        },
        {
            variant: "outline",
            color: "primary",
            class: [
                "ko:text-primary",
                "ko:data-hover:bg-primary-tint ko:data-focus:bg-primary-tint",
                "ko:data-active:bg-primary-tint-strong",
                "ko:data-hover:data-active:bg-primary-tint-strong",
                "ko:data-disabled:text-primary",
            ],
        },
        {
            variant: "outline",
            color: "secondary",
            class: [
                "ko:text-secondary",
                "ko:data-hover:bg-secondary-tint ko:data-focus:bg-secondary-tint",
                "ko:data-active:bg-secondary-tint-strong",
                "ko:data-hover:data-active:bg-secondary-tint-strong",
                "ko:data-disabled:text-secondary",
            ],
        },
        {
            variant: "outline",
            color: "neutral",
            class: [
                "ko:text-neutral",
                "ko:data-hover:bg-neutral-tint ko:data-focus:bg-neutral-tint",
                "ko:data-active:bg-neutral-tint-strong",
                "ko:data-hover:data-active:bg-neutral-tint-strong",
                "ko:data-disabled:text-neutral",
            ],
        },
        {
            variant: "link",
            color: "primary",
            class: [
                "ko:text-primary",
                "ko:data-hover:bg-primary-tint ko:data-hover:text-primary-hover ko:data-focus:text-primary-hover",
                "ko:data-active:text-primary-active ko:data-active:bg-primary-tint",
                "ko:data-hover:data-active:text-primary-active",
                "ko:data-disabled:text-primary",
            ],
        },
        {
            variant: "link",
            color: "secondary",
            class: [
                "ko:text-secondary",
                "ko:data-hover:bg-secondary-tint ko:data-hover:text-secondary-hover ko:data-focus:text-secondary-hover",
                "ko:data-active:text-secondary-active ko:data-active:bg-secondary-tint",
                "ko:data-hover:data-active:text-secondary-active",
                "ko:data-disabled:text-secondary",
            ],
        },
        {
            variant: "link",
            color: "neutral",
            class: [
                "ko:text-neutral",
                "ko:data-hover:bg-neutral-tint ko:data-hover:text-text-strong ko:data-focus:text-text-strong",
                "ko:data-active:text-text-strong ko:data-active:bg-neutral-tint",
                "ko:data-hover:data-active:text-text-strong",
                "ko:data-disabled:text-neutral",
            ],
        },
        {
            variant: "answer",
            color: "primary",
            class: [
                "ko:border-primary-tint-strong ko:text-text-strong",
                "ko:hover:bg-primary-tint",
                "ko:data-[just-clicked]:hover:!bg-transparent",
                "ko:data-checked:border-primary ko:data-checked:bg-primary ko:data-checked:text-on-bg-primary",
                "ko:data-checked:hover:bg-transparent ko:data-checked:hover:text-text-strong ko:data-checked:active:bg-primary-tint-strong ko:data-checked:active:border-primary",
                "ko:data-checked:data-[just-clicked]:hover:!bg-primary ko:data-checked:data-[just-clicked]:hover:!text-on-bg-primary",
                "ko:data-active:bg-primary-tint-strong",
            ],
        },
        {
            variant: "answer",
            color: "secondary",
            class: [
                "ko:border-secondary-tint-strong ko:text-text-strong",
                "ko:hover:bg-secondary-tint",
                "ko:data-[just-clicked]:hover:!bg-transparent",
                "ko:data-checked:border-secondary ko:data-checked:bg-secondary ko:data-checked:text-on-bg-secondary",
                "ko:data-checked:hover:bg-transparent ko:data-checked:hover:text-text-strong ko:data-checked:active:bg-secondary-tint-strong ko:data-checked:active:border-secondary",
                "ko:data-checked:data-[just-clicked]:hover:!bg-secondary ko:data-checked:data-[just-clicked]:hover:!text-on-bg-secondary",
                "ko:data-active:bg-secondary-tint-strong",
            ],
        },
        {
            variant: "answer",
            color: "neutral",
            class: [
                "ko:border-neutral-tint-strong ko:text-text-strong",
                "ko:hover:bg-neutral-tint",
                "ko:data-[just-clicked]:hover:!bg-transparent",
                "ko:data-checked:border-neutral ko:data-checked:bg-neutral ko:data-checked:text-on-bg-neutral",
                "ko:data-checked:hover:bg-transparent ko:data-checked:hover:text-text-strong ko:data-checked:active:bg-neutral-tint-strong ko:data-checked:active:border-neutral",
                "ko:data-checked:data-[just-clicked]:hover:!bg-neutral ko:data-checked:data-[just-clicked]:hover:!text-on-bg-neutral",
                "ko:data-active:bg-neutral-tint-strong",
            ],
        },
        // Icon-only "answer" (the recap row's compact yes/no toggles): the variant's own clamp() is sized for
        // the big question-card Ano/Ne pair, so `size="small"` needs its own override here the same way
        // `round`+`small` already does above — 32-36px so a title next to it keeps to one or two lines.
        {
            variant: "answer",
            size: "small",
            class: ["ko:h-9 ko:w-9"],
        },
        {
            variant: "round",
            class: ["ko:border-border ko:text-text-strong", "ko:hover:bg-neutral-tint", "ko:data-active:bg-neutral-tint-strong"],
        },
        {
            variant: "round",
            color: "primary",
            class: ["ko:data-checked:border-primary ko:data-checked:bg-primary ko:data-checked:text-on-bg-primary"],
        },
        {
            variant: "round",
            color: "secondary",
            class: ["ko:data-checked:border-secondary ko:data-checked:bg-secondary ko:data-checked:text-on-bg-secondary"],
        },
        {
            variant: "round",
            color: "neutral",
            class: ["ko:data-checked:border-neutral ko:data-checked:bg-neutral ko:data-checked:text-on-bg-neutral"],
        },
        {
            variant: "pill",
            class: ["ko:border-border ko:text-text-strong", "ko:hover:bg-surface-hover ko:data-hover:bg-surface-hover", "ko:data-active:bg-surface-hover", "ko:data-disabled:border-border"],
        },
        // `round`'s own clamp() sizes it for the star toggle next to the answer
        // buttons. The header close/back icon button is a fixed 44px touch
        // target instead — pass `size="small"` for that case.
        {
            variant: "round",
            size: "small",
            class: ["ko:h-11 ko:w-11"],
        },
        // 36px, the same as the recap row's icon-only answer toggles, so the star and the answers read as one set.
        {
            variant: "round",
            size: "xsmall",
            class: ["ko:h-9 ko:w-9"],
        },
    ],
});
function ButtonComponent({ children, size, variant, color, ...props }, ref) {
    const isIconOnly = react_2.default.isValidElement(children) && children.type === icon_1.Icon;
    const iconOnlyClasses = isIconOnly ? "ko:aspect-square ko:!p-0 ko:!rounded-full ko:grid ko:place-items-center" : "";
    // The answer variant is always rendered icon-only (✓/✕). 2026 draws that
    // mark noticeably larger than our Icon default (28px vs. the medium 24px) —
    // bump it locally here rather than asking every call site to pass a size.
    // At size="small" (the recap row's compact toggles, 36px circles) the mark scales down to match.
    const answerIconClasses = isIconOnly && variant === "answer" ? (size === "small" ? "ko:[&>svg]:!size-4" : "ko:[&>svg]:!size-7") : "";
    return ((0, jsx_runtime_1.jsx)(react_1.Button, { className: (0, utilities_1.twMerge)((0, exports.ButtonVariants)({ size, variant, color }), iconOnlyClasses, answerIconClasses), ...props, ref: ref, children: children }));
}
const Button = react_2.default.forwardRef(ButtonComponent);
exports.Button = Button;
